/**
 * The admin realm (ADR-007), as this console reads it. The shapes are the API's schemas
 * (`./schemas`); the hand transcription this file used to hold had described an
 * `AdminIdentity.displayName` and a session shaped `{token, expiresAt}`, neither of which
 * the API ever sent.
 */
import type { Schemas } from './schemas';

/** Who is signed in. This, and nothing more, is what the console renders a shell for. */
export type AdminIdentity = Schemas['AdminDto'];

/** A member of the admin team, as `GET /admin/admins` returns them. Same shape as the identity. */
export type AdminTeamMember = AdminIdentity;

/** One platform policy switch — a row of the Settings page's audit-policy list. */
export type Policy = Schemas['PolicyDto'];

/** The one-time password `POST /admin/admins` returns — shown once, never stored in the clear. */
export type InvitedAdmin = Schemas['CreatedAdminDto'];

export type AdminTotpEnrolment = Schemas['AdminTotpEnrolmentDto'];

/** The first step of a sign-in: a challenge for the second factor, with enrolment if needed. */
export type AdminLoginChallengeResponse = Schemas['AdminLoginChallengeDto'];

export type AdminSessionResponse = Schemas['AdminSessionDto'];

/**
 * Why the TOTP step refused, in its 401's `details.reason` (Backend#161): a challenge
 * no code can answer any more, or a code worth asking for again.
 */
export type AdminTotpRefusalReason = Schemas['AdminTotpRefusalReason'];

/**
 * The sidebar badges (C5). Both numbers are counts of work waiting on a human, which is
 * why they are read live on every console render rather than cached — a badge that lags
 * the queue it describes is worse than no badge.
 */
export type AdminPendingCounts = Schemas['AdminPendingCountsDto'];

export type AdminNotificationItem = Schemas['AdminNotificationDto'];

/** The request bodies of the admin realm's own calls. */
export type AdminLoginBody = Schemas['AdminLoginDto'];
export type AdminTotpVerifyBody = Schemas['AdminTotpVerifyDto'];
export type AdminMfaUpdateBody = Schemas['AdminMfaUpdateDto'];
export type CreateAdminBody = Schemas['CreateAdminDto'];
export type DisableAdminBody = Schemas['DisableAdminDto'];
export type UpdatePolicyBody = Schemas['UpdatePolicyDto'];

/** Every failure the API answers: `{ error: { code, message, details }, meta }`. */
export type ApiErrorEnvelope = Schemas['ErrorEnvelopeDto'];
/** The `error` inside it. `message` is developer-facing English and is never rendered. */
export type ApiErrorBody = Schemas['ErrorBodyDto'];

/**
 * Why a sign-in or a request failed, as the console names the outcome.
 *
 * Console vocabulary, not an API shape. The API's failures are `ErrorEnvelopeDto`, whose
 * `error.code` is drawn from its own `ErrorCode`; `lib/http.ts` reads that envelope and
 * maps it onto one of these, and the sign-in calls in `lib/endpoints/auth.ts` name what a
 * 401 means on their own route. The console renders the sentence for each, because the
 * sentence is Arabic and Arabic lives in the message catalogs (ADR-013).
 */
export const ADMIN_AUTH_ERROR = {
  INVALID_CREDENTIALS: 'invalid_credentials',
  ACCOUNT_LOCKED: 'account_locked',
  /** The API's own `rate_limited`, for a throttle that says nothing of a lockout. */
  RATE_LIMITED: 'rate_limited',
  /** The TOTP step's `challenge_expired`: no code can answer it. The login restarts from the password. */
  CHALLENGE_INVALID: 'challenge_invalid',
  INVALID_TOTP: 'invalid_totp',
  SESSION_INVALID: 'session_invalid',
  UNAVAILABLE: 'unavailable',
} as const;

export type AdminAuthErrorCode = (typeof ADMIN_AUTH_ERROR)[keyof typeof ADMIN_AUTH_ERROR];

/**
 * Two initials for the avatar, from the display name.
 *
 * Arabic carries no capitalization, so this takes the first letter of the first two
 * words — but it drops a leading definite article first. Without that, every family name
 * starting with «ال» initials to «ا» and half the admin team shares an avatar: the
 * prototype's «وليد السنوسي» is «و س», not «و ا». Shared because the sidebar footer and
 * the header avatar must never disagree.
 */
/**
 * Initials for the avatar in the header.
 *
 * @i18n-exempt — `ال` is the Arabic definite article, stripped so that وليد السنوسي
 * initials to "و س" rather than "و ا", which is what most Arabic surnames would give.
 * That is language data, not copy: there is nothing for a translator to do with it.
 *
 * The `؟` fallback for an empty name is the one string here that *is* rendered. It is
 * locale-specific — an English console should show `?` — and it stays a known, small
 * defect until `en` is actually switched on (ADR-013), rather than a message key for a
 * single punctuation mark.
 */
export function adminInitials(displayName: string): string {
  const words = displayName
    .trim()
    .split(/\s+/)
    .map((word) => (word.length > 3 && word.startsWith('ال') ? word.slice(2) : word))
    .filter(Boolean);
  if (words.length === 0) return '؟';
  return words
    .slice(0, 2)
    .map((word) => [...word][0])
    .join(' ');
}
